import React, { useState, useEffect, useMemo } from 'react';
import { Table, Button, Spin, Alert, Tooltip, Input, Modal, Radio, Space, message, Badge } from 'antd';
import { FilePdfOutlined, FileZipOutlined, SearchOutlined, EyeOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { faFileArrowUp, faFile } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import 'bootstrap/dist/css/bootstrap.min.css';

import { useSelector } from 'react-redux';
import { selectAppPermissions } from '../../store/auth/selectors';
import { getFilesMI, descargaSoportes, deleteFileMI } from '../../services/plaOperativo/planOperativoProyectosServices';
import ModalSubirSoporte from '../ManifestacionInteres/ModalSubirSoporte';

const { Search } = Input;

const ManifestacionInteres = ({ proyectoCodigo, codP, nameP }) => {
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);


    const [searchText, setSearchText] = useState('');
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [selectedZone, setSelectedZone] = useState('Todos');
    const [downloadLoading, setDownloadLoading] = useState(false);
    const [isModalSubirVisible, setIsModalSubirVisible] = useState(false);
    const [subcarpetaDestino, setSubcarpetaDestino] = useState('');
    const [modoMultiple, setModoMultiple] = useState(false);
    const [originalData, setOriginalData] = useState([]);
    const [filtroActivo, setFiltroActivo] = useState(false);

    // Permissions
    const permissions = useSelector(state => selectAppPermissions(state, 'ManifestacionInteres'));
    const permissionUpdate = permissions.priv_update === 'Y';
    const permissionDelete = permissions.priv_delete === 'Y';
    const permissionInsert = permissions.priv_insert === 'Y';
    const permissionExport = permissions.priv_export === 'Y';
    const permissionPrint = permissions.priv_print === 'Y';
    const user = useSelector((state) => state.auth.user);
    const userLogin = user.username;

    useEffect(() => {
        const fetchFiles = async () => {
            if (!proyectoCodigo) return;
            setLoading(true);
            setError(null);
            try {
                const { status, data: responseData } = await getFilesMI(proyectoCodigo);
                if (status === 200) {
                    const tableData = responseData.map(item => ({
                        key: item.oid,
                        documento: item.nameResource,
                        usuario: item.user,
                        fecha: item.fecha_documento,
                        fullPath: item.fullPath,
                        detailJSON: item.detailJSON
                    }));
                    if (tableData.length === 0) {
                        const carpetaProyecto = `P${proyectoCodigo}`;
                        const carpetaPath = `DocumentosMI/${proyectoCodigo}/${carpetaProyecto}/`;

                        // Insertamos una entrada que solo representa una carpeta vacía
                        tableData.push({
                            key: `grupo-${carpetaProyecto}`,
                            fullPath: carpetaPath,
                            isEmptyGroup: true // lo puedes usar para excluir en render si deseas
                        });
                    }

                    setData(tableData);
                    setOriginalData(tableData);
                } else {
                    setError('No se pudieron obtener los archivos.');
                }
            } catch (err) {
                console.error(err);
                setError('Error al conectar con el servidor');
            } finally {
                setLoading(false);
            }
        };
        fetchFiles();
    }, [proyectoCodigo]);

    // Filtrado de búsqueda
    const filteredData = useMemo(() =>
        data.filter(item =>
            item.isEmptyGroup || (
                typeof item.documento === 'string' &&
                item.documento.toLowerCase().includes(searchText.toLowerCase())
            )
        ),
        [data, searchText]
    );


    const limpiarNombreDocumentoCodigo = (nombre) => {
        if (typeof nombre !== 'string') {
            console.warn('Se esperaba un string pero se recibió:', nombre);
            return '';
        }

        const indexGuion = nombre.indexOf('-');

        return indexGuion !== -1 ? nombre.substring(indexGuion + 1) : nombre;
    };



    const buildFileTree = (fileList, limpiarNombreDocumentoCodigo) => {
        const root = {};
        for (const item of fileList) {
            const parts = item.fullPath.replace(/\/$/, '').split('/').slice(2); // omitimos DocumentosMI y el ID del proyecto
            let current = root;

            for (let i = 0; i < parts.length; i++) {
                const part = parts[i];

                if (!current[part]) {
                    current[part] = {
                        __children: {},
                        __isGroup: true
                    };
                }

                if (i === parts.length - 1 && !item.isEmptyGroup) {
                    // Es un archivo, no una carpeta vacía
                    current[part] = {
                        ...item,
                        documento: item.documento,
                        fullPath: item.fullPath
                    };
                } else {
                    current = current[part].__children || {};
                }
            }
        }

        const toDataSource = (node, parentKey = '') => {
            return Object.entries(node).map(([name, value]) => {
                const key = parentKey ? `${parentKey}/${name}` : name;

                if (value.__isGroup) {
                    const children = toDataSource(value.__children || {}, key);
                    return {
                        key,
                        rawName: name,
                        isGroup: true,
                        documento: (
                            <div
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    paddingLeft: `${key.split('/').length * 12}px`,
                                }}
                            >
                                <span style={{ display: 'flex', alignItems: 'center' }}>
                                    {name}
                                    <Badge
                                        count={children.length}
                                        style={{ backgroundColor: '#52c41a', marginLeft: 10 }}
                                        showZero
                                    />
                                </span>
                            </div>

                        ),
                        children
                    };
                } else {
                    return {
                        ...value,
                        documento: (
                            <span style={{ display: 'flex', alignItems: 'center', whiteSpace: 'normal' }}>
                                <FilePdfOutlined style={{ color: 'red', marginRight: 4 }} />
                                {limpiarNombreDocumentoCodigo(value.documento)}
                            </span>
                        )
                    };
                }
            }).sort((a, b) => a.rawName?.localeCompare(b.rawName || '') ?? 0);
        };

        return toDataSource(root);
    };

    // Agrupamiento para el proyecto 11
    const tableData = useMemo(() => {
        return buildFileTree(filteredData, limpiarNombreDocumentoCodigo);
    }, [filteredData]);

    const handleView = (record) => {
        const path = record.fullPath.startsWith('/')
            ? record.fullPath
            : '/' + record.fullPath;

        window.open(path, '_blank');
    };

    const handleEdit = record => console.log('Editar:', record);
    const handleDelete = (record) => {
        const nombreArchivo = record.fullPath.split('/').pop();
        const nombreLimpio = nombreArchivo.includes('-')
            ? nombreArchivo.split('-').slice(1).join('-')
            : nombreArchivo;

        Modal.confirm({
            title: '¿Estás seguro que deseas eliminar este archivo?',
            content: `Nombre: ${nombreLimpio}`,
            okText: 'Sí, eliminar',
            cancelText: 'Cancelar',
            onOk: async () => {
                try {
                    await deleteFileMI(record.key, userLogin);
                    message.success('Archivo eliminado correctamente');
                    setData(prevData => prevData.filter(item => item.key !== record.key));
                } catch (error) {
                    console.error(error);
                    message.error('Error al eliminar el archivo');
                }
            },
        });
    };

    const handleCerrarModal = () => {
        setIsModalSubirVisible(false);
        setModoMultiple(false);
    };

    const handleDownloadClick = () => {
        if (proyectoCodigo === 11) {
            setIsModalVisible(true);
        } else {
            setDownloadLoading(true);
            descargaSoportes(proyectoCodigo, '')
                .catch(() => message.error('No se pudieron descargar los soportes.'))
                .finally(() => setDownloadLoading(false));
        }
    };

    const handleModalOk = async () => {
        if (!selectedZone) {
            message.warning('Selecciona una subdivisión.');
            return;
        }
        setDownloadLoading(true);
        try {
            if (selectedZone === 'Todos') {
                await descargaSoportes(proyectoCodigo, '');
            } else {
                await descargaSoportes(proyectoCodigo, selectedZone);
            }
            setIsModalVisible(false);
        } catch {
            message.error('No se pudieron descargar los soportes.');
        } finally {
            setDownloadLoading(false);
        }
    };

    const zoneOptions = ['Arauquita', 'Fortul', 'Saravena', 'Tame', 'Todos'];

    const columns = [
        {
            title: 'Acciones',
            key: 'actions',
            width: 120,
            align: 'center',
            render: (_text, record) => {
                if (record.isGroup) {
                    return (
                        <div style={{ display: 'flex', gap: 8, justifyContent: 'center' }}>
                            {permissionInsert && (
                                <Tooltip title="Subir un soporte">
                                    <Button
                                        size="middle"
                                        type="primary"
                                        icon={<FontAwesomeIcon icon={faFile} />}
                                        onClick={() => {
                                            setSubcarpetaDestino(record.key);
                                            setIsModalSubirVisible(true);
                                            setModoMultiple(false);
                                        }}
                                    />
                                </Tooltip>
                            )}
                            {permissionPrint && (
                                <Tooltip title="Subir múltiples soportes">
                                    <Button
                                        size="middle"
                                        type="default"
                                        icon={<FontAwesomeIcon icon={faFileArrowUp} style={{ color: 'green', fontSize: "18px" }} />}
                                        onClick={() => {
                                            setSubcarpetaDestino(record.key);
                                            setIsModalSubirVisible(true);
                                            setModoMultiple(true);
                                        }}
                                    />
                                </Tooltip>
                            )}
                        </div>
                    );
                }
                return (
                    <div style={{ display: 'flex', gap: 10, justifyContent: 'center' }}>
                        <Tooltip title="Ver soporte">
                            <Button
                                type="primary"
                                size="middle"
                                icon={<EyeOutlined />}
                                onClick={() => handleView(record)}
                            />
                        </Tooltip>
                        {permissionUpdate && (
                            <Tooltip title="Editar">
                                <Button
                                    type="default"
                                    size="middle"
                                    icon={<EditOutlined />}
                                    onClick={() => handleEdit(record)}
                                />
                            </Tooltip>
                        )}
                        {permissionDelete && (
                            <Tooltip title="Eliminar">
                                <Button
                                    danger
                                    size="middle"
                                    icon={<DeleteOutlined />}
                                    onClick={() => handleDelete(record)}
                                />
                            </Tooltip>
                        )}
                    </div>
                );
            }
        },
        { title: 'Documento', dataIndex: 'documento', key: 'documento', align: 'left', width: 250, render: (text, record) => text },
        { title: 'Usuario', dataIndex: 'usuario', key: 'usuario', align: 'center', width: 70, render: (text, record) => record.isGroup ? null : text },
        { title: 'Fecha', dataIndex: 'fecha', key: 'fecha', align: 'center', width: 100, render: (fecha, record) => record.isGroup ? null : (fecha ? fecha.split('T')[0] : '') }
    ];

    const handleFiltrarRepetidos = () => {
        if (filtroActivo) {
            message.info("Ya estás viendo los repetidos. Usa 'Limpiar filtro' para volver.");
            return;
        }

        try {
            const contadorNumerosFinales = {};

            // 🧩 Buscar el número al final del nombre antes del .pdf
            data.forEach(item => {
                const match = item.documento?.match(/(\d+)(?=\.pdf$)/i);
                if (match) {
                    const numeroFinal = match[1];
                    contadorNumerosFinales[numeroFinal] = (contadorNumerosFinales[numeroFinal] || 0) + 1;
                }
            });

            // 🔍 Filtrar solo los que se repiten
            const repetidos = data.filter(item => {
                const match = item.documento?.match(/(\d+)(?=\.pdf$)/i);
                if (match) {
                    const numeroFinal = match[1];
                    return contadorNumerosFinales[numeroFinal] > 1;
                }
                return false;
            });

            if (repetidos.length === 0) {
                message.warning("No se encontraron archivos repetidos.");
                return;
            }

            // 🧠 Ordenar los repetidos por número y luego por fecha
            const repetidosOrdenados = repetidos.sort((a, b) => {
                const numeroA = a.documento?.match(/(\d+)(?=\.pdf$)/i)?.[1] || "";
                const numeroB = b.documento?.match(/(\d+)(?=\.pdf$)/i)?.[1] || "";

                if (numeroA !== numeroB) {
                    return numeroA.localeCompare(numeroB, undefined, { numeric: true });
                }
                return new Date(b.fecha) - new Date(a.fecha);
            });

            setData(repetidosOrdenados);
            setFiltroActivo(true);
            message.success(
                `Mostrando ${repetidosOrdenados.length} archivos repetidos basados en el número final.`
            );
        } catch (error) {
            console.error("Error al filtrar repetidos:", error);
            message.error("Error al filtrar los archivos.");
        }
    };



    const handleLimpiarFiltro = () => {
        if (!filtroActivo) {
            message.info("No hay ningún filtro activo.");
            return;
        }

        setData(originalData);
        setFiltroActivo(false);
        message.success("Filtro limpiado. Mostrando todos los archivos.");
    };

    return (
        <>
            <div className="container-fluid px-0 mt-2">
                <div className="mb-4 d-flex justify-content-between align-items-center">
                    <p className="mb-0">Documentos de MI proyecto {codP} - {nameP}.</p>
                    <Space>
                        {permissionExport && (
                            <Button
                                type="primary"
                                icon={<FileZipOutlined />}
                                onClick={handleDownloadClick}
                                loading={downloadLoading}
                            >
                                Descargar
                            </Button>
                        )}
                        <Button
                            type="default"
                            onClick={handleFiltrarRepetidos}
                            disabled={filtroActivo}
                        >
                            Filtrar repetidos
                        </Button>
                        <Button
                            type="dashed"
                            onClick={handleLimpiarFiltro}
                            disabled={!filtroActivo}
                        >
                            Limpiar filtro
                        </Button>
                        <Search
                            placeholder="Buscar documento"
                            suffix={<SearchOutlined style={{ color: 'red', fontSize: 16 }} />}
                            allowClear
                            onChange={e => setSearchText(e.target.value)}
                            style={{ width: 300 }}
                        />
                    </Space>


                </div>
                {error && <Alert message="Error" type="error" showIcon className="mb-3" />}
                {loading ? <div className="text-center"><Spin /></div> : (
                    <Table style={{ width: '100%' }} tableLayout="fixed" scroll={{ x: 620 }} columns={columns} dataSource={tableData} pagination={{ pageSize: 10 }} rowKey={record => record.isGroup ? record.key : record.key} />
                )}
            </div>
            <Modal
                title="Guardar soportes en el equipo"
                open={isModalVisible}
                confirmLoading={downloadLoading}
                onCancel={() => setIsModalVisible(false)}
                onOk={handleModalOk}
                okText="Descargar"
                cancelText="Cancelar"
                destroyOnHidden
                styles={{ mask: { backgroundColor: 'rgba(0, 0, 0, 0.85)' } }}
            >
                <p>Selecciona la subdivisión a descargar:</p>
                <Radio.Group onChange={e => setSelectedZone(e.target.value)} value={selectedZone} style={{ marginTop: 16 }}>
                    {zoneOptions.map(zone => <Radio key={zone} value={zone}>{zone}</Radio>)}
                </Radio.Group>
            </Modal>
            <ModalSubirSoporte
                visible={isModalSubirVisible}
                onCancel={handleCerrarModal}
                oidProyecto={proyectoCodigo}
                rutaDestino={subcarpetaDestino}
                user={userLogin}
                onArchivoSubido={nuevoArchivo => {
                    setData(prevData => [...prevData, nuevoArchivo]);
                }}
                modoMultiple={modoMultiple}

            />
        </>
    );
};

export default ManifestacionInteres;
